<template>
  <div class="jwl-facilitator-grading-overview">
    <FacilitatorGradingClassroomSelector
      @selected="getSubmittedGrades($event)" />

    <div v-if="loadingState !== 0">
      <!-- byTasks -->
      <div class="jwl-facilitator-grading-overview__row">
        <h4 v-t="'home.facilitatorGrading.filter.task'" />
        <ul class="jwl-facilitator-grading-overview__filter-by-task-list">
          <li v-for="taskType in taskTypes" :key="taskType" :title="taskType">
            <router-link
              class="jwl-facilitator-grading-overview__button"
              :to="detailRoute(taskType, 'all')">
              <span class="jwl-facilitator-grading-overview__icon">
                <TaskIcon :task-type="taskType" use-duotone/>
              </span>
              <span class="jwl-facilitator-grading-overview__title" v-t="`task.type.${taskType}`"></span>
            </router-link>
          </li>
        </ul>
      </div>
      <div class="jwl-facilitator-grading-overview__row--sub">
        <div class="jwl-facilitator-grading-overview__sub--headline">
          <h5 v-t="'home.facilitatorGrading.ungraded-tasks'" /> {{ selectedClassroom.countNotGraded }}
        </div>
        <ul class="jwl-facilitator-grading-overview__filter-by-task-list--sub">
          <li v-for="taskType in taskTypes" :key="taskType" :title="taskType">
            <router-link
                class="jwl-facilitator-grading-overview__button"
                :to="detailRoute(taskType, 'notGraded')">
              <span class="jwl-facilitator-grading-overview__counter" v-if="getCounterForTaskType(taskType)">
                {{getCounterForTaskType(taskType).countNotGraded}}
              </span>
            </router-link>
          </li>
        </ul>
      </div>
      <div class="jwl-facilitator-grading-overview__row--sub">
        <div class="jwl-facilitator-grading-overview__sub--headline">
          <h5 v-t="'home.facilitatorGrading.graded-unpublished'" /> {{ selectedClassroom.countGradeNotPublished }}
        </div>
        <ul class="jwl-facilitator-grading-overview__filter-by-task-list--sub">
          <li v-for="taskType in taskTypes" :key="taskType" :title="taskType">
            <router-link
                class="jwl-facilitator-grading-overview__button"
                :to="detailRoute(taskType, 'gradedUnpublished')">
              <span class="jwl-facilitator-grading-overview__counter" v-if="getCounterForTaskType(taskType)">
                {{getCounterForTaskType(taskType).countGradeNotPublished}}
              </span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="jwl-facilitator-grading-overview__row--bottom">
        <JwlButton class="jwl-facilitator-grading-overview__task-all-link JwlButton" :to-route="selectAllTasks" :normal-button-style="true">
          <span v-t="'home.facilitatorGrading.grade-all'" />
        </JwlButton>
      </div>

      <div v-if="loadingState === 1">
        <JwlIcon loading-icon margin-right />
        {{t('common.loadingData')}}
      </div>

      <JwlError v-else-if="loadingState === -1" :jwl-error="error" />

      <!-- byStudents-->
      <FacilitatorGradingStudentList
        v-else-if="loadingState > 1"
        class="jwl-facilitator-grading-overview__row"
        :key="classroomId"
        :classroom-id="classroomId"
        :course-code="courseCode"
        :selected-classroom="selectedClassroom"
        @student-clicked="goToStudent($event)" />
    </div>
  </div>
</template>

<script setup>
  const learningStore = useLearningStore();

  const { t } = useI18n();
  const router = useRouter();

  const classroomId = ref(null);
  const courseCode = ref(null);
  const taskTypes = ['submit_work', 'submit_text', 'discuss', 'track', 'attendance', 'quiz', 'final_examination'];
  const programmeType = ref('professional');
  const selectedClassroom = ref(null);
  const facilitatorInterfaces = ref([]);
  const submittedItems = ref(null);
  const loadingState = ref(0);
  const error = ref(null);
  const totalUngradedCount = ref(null);
  const totalGradedButUnpublishedCount = ref(null);
  const gradableStudentIdsCount = ref(null);
  const gradableStudentsByClc = ref(null);

  const getSubmittedGrades = (args) => {
    loadingState.value = 1;
    selectedClassroom.value = args;
    classroomId.value = args.id;
    courseCode.value = args.course.courseCode;
    programmeType.value = args.course.programmeType;
    submittedItems.value = null;
    learningStore.getData(`facilitator/classroom/${classroomId.value}/course/${courseCode.value}/submitted-grades`)
      .then((data) => {
        submittedItems.value = data.gradableItems;
        totalUngradedCount.value = data.totalUngradedCount;
        totalGradedButUnpublishedCount.value = data.totalGradedButUnpublishedCount;
        gradableStudentIdsCount.value = data.gradableStudentIdsCount;
        loadingState.value = 2;
      })
      .catch((e) => {
        error.value = e;
        loadingState.value = -1;
      });
  };

  const getCounterForTaskType = (taskType) => {
    if (selectedClassroom.value) {
      if (selectedClassroom.value.taskCounter[taskType]) {
        return selectedClassroom.value.taskCounter[taskType];
      }
    }

    return {
      countGradeNotPublished: 0,
      countNotGraded: 0,
    };
  };

  const detailRoute = (taskType, status) => {
    if (classroomId.value && courseCode.value) {
      return {
        name: 'facilitator_grading_tasks',
        params: {
          classroomId: classroomId.value.toString(),
          course: courseCode.value,
        },
        query: {
          filterByTaskType: taskType,
          filterByStatus: status,
        },
      };
    }

    return null;
  };

  const goToStudent = (jwlId) => {
    if (classroomId.value && courseCode.value) {
      router.push({
        name: 'facilitator_grading_students',
        params: {
          jwlId,
          class: classroomId.value.toString(),
          course: courseCode.value,
        },
      });
    }
  };

  const selectAllTasks = computed(() => {
    if (classroomId.value && courseCode.value) {
      return {
        name: 'facilitator_grading_tasks',
        params: {
          classroomId: classroomId.value.toString(),
          course: courseCode.value,
        },
        query: {
          filterByTaskType: 'all',
        },
      };
    }
    return null;
  });

  const rootClass = computed(() => ({
    'jwl-facilitator-grading-overview--bachelor': programmeType.value === 'bachelor',
    'jwl-facilitator-grading-overview--professional': programmeType.value === 'professional',
    'jwl-facilitator-grading-overview--language': programmeType.value === 'language',
  }));
</script>

<style lang="scss">
@use '@base/styles/mixins';
@use '@base/styles/variables';

.jwl-facilitator-grading-overview {
  @include mixins.modifierVars();

  --concentration-color: var(--color-black);

  &__row, &__row--sub {
    align-items: flex-start;
    display: grid;
    grid-template-columns: 14.5rem 1fr;
    h5{
      margin: 0;
    }
  }

  &__row--sub {
    margin-bottom: .5rem;
  }

  &__row--bottom{
    margin-top: 1rem;
    display: flex;
    justify-content: flex-end;
  }

  &__task-all-link{
    --primary-color: var(--color-blue);
    --border-color: var(--color-blue);
  }

  &__sub--headline{
    padding: 0.5rem;
    background-color: var(--color-gray-100);
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    h5{
      text-decoration: underline;
    }
  }

  &__filter-by-task-list, &__filter-by-task-list--sub {
    display: flex;
    flex-flow: row nowrap;
    list-style: none;
    padding-left: 0;
    justify-content: space-between;
    min-width: 500px;
    li{
      width: 14.2%;
    }
  }

  &__filter-by-task-list--sub {
    margin: 0.4rem 0;
  }

  &__button {
    align-items: center;
    color: var(--concentration-color);
    cursor: pointer;
    display: flex;
    flex-flow: column;
    justify-content: center;
    text-decoration: none;
  }

  &__title {
    margin-top: .5rem;
    text-decoration: underline;
    text-align: center;
  }

  &__counter{
    color: var(--color-blue);
    text-decoration: underline;
  }

  &__icon {
    align-items: center;
    background-color: var(--color-white);
    border: 1px solid var(--concentration-color);
    border-radius: 50%;
    box-sizing: border-box;
    display: flex;
    font-size: 1.5rem;
    height: 3rem;
    justify-content: center;
    margin: 0 .2rem;
    transition: background-color .25s, color .25s;
    width: 3rem;

    &:hover {
      background-color: var(--concentration-color);
      color: var(--color-white);
    }
  }
}
</style>
